// import AccountBoxTwoToneIcon from "@mui/icons-material/AccountBoxTwoTone";
// import AccountTreeTwoToneIcon from "@mui/icons-material/AccountTreeTwoTone";
import ExpandMoreTwoToneIcon from "@mui/icons-material/ExpandMoreTwoTone";
import LoginIcon from "@mui/icons-material/Login";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import {
  Avatar,
  Box,
  Card,
  Divider,
  Hidden,
  List,
  ListItem,
  ListItemText,
  Popover,
  Typography,
  lighten,
  useTheme
} from "@mui/material";
import { styled } from "@mui/material/styles";

import { useRef, useState } from "react";
import { useSelector } from "react-redux";

import CButton from "@/components/Button";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ThemeSwitcher } from "@/components/ThemeSwitcher";
import { PATH } from "@/configs/router/routerConfig";
import { hasPendingWorkspaceSelection } from "@/hooks/useSignupProvisioning";
import { logger } from "@/lib/logger";
import { buildSignupSetupPath } from "@/lib/signup/setupRouting";
import { logOutUser } from "@/slices/auth/authThunk";
import { useDispatch } from "@/store";
import { rootReducerName } from "@/store/rootReducer";
import { getPersonDisplayName } from "@/utils/format/nameFormatter";
import { isTenantAdmin } from "@/utils/permissions";
import { resolveMediaUrl } from "@/utils/resolveMediaUrl";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

const UserBoxButton = styled(CButton)(
  ({ theme }) => `
        padding-left: ${theme.spacing(1)};
        padding-right: ${theme.spacing(1)};
`
);

const MenuUserBox = styled(Box)(
  ({ theme }) => `
        background: ${theme.colors.alpha.black[5]};
        padding: ${theme.spacing(1, 2)};
`
);

const UserBoxText = styled(Box)(
  ({ theme }) => `
        text-align: left;
        padding-left: ${theme.spacing(1)};
`
);

const UserBoxLabel = styled(Typography)(
  ({ theme }) => `
        font-weight: ${theme.typography.fontWeightBold};
        color: ${theme.palette.secondary.main};
        display: block;
`
);

const UserBoxDescription = styled(Typography)(
  ({ theme }) => `
    color: ${lighten(theme.palette.secondary.main, 0.5)}
`
);

type PendingWorkspaceUser = {
  pendingWorkspaceSelection?: boolean;
  spPendingWorkspaceSelection?: boolean;
  tenantKind?: string;
  tenant_kind?: string;
  spTKind?: string;
};

export const resolveProfileHref = (user: PendingWorkspaceUser): string => {
  const pendingWorkspaceSelection = hasPendingWorkspaceSelection(user);
  return pendingWorkspaceSelection ? buildSignupSetupPath(PATH.PROFILE) : PATH.PROFILE;
};

function HeaderUserbox() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const router = useRouter();
  const auth = useSelector((state) => state[rootReducerName.auth]);
  const user = auth.user;

  const ref = useRef<any>(null);
  const [isOpen, setOpen] = useState<boolean>(false);

  const theme = useTheme();

  if (!user) {
    return null;
  }

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const handleLogout = async (): Promise<void> => {
    try {
      handleClose();
      await dispatch(logOutUser({}) as any);
    } catch (err) {
      logger.error("err: ", err);
    }
  };

  const handleProfileClick = (): void => {
    handleClose();
    void router.push(profileHref);
  };

  const workspaceLabel = String((user as { tenantDisplayString?: string })?.tenantDisplayString || "").trim();
  const roleLabel = isTenantAdmin(auth) ? t("Admin") : t("User");
  const workspaceDescription = workspaceLabel ? `${roleLabel} · ${t("Workspace")}: ${workspaceLabel}` : roleLabel;
  const profileHref = resolveProfileHref(user as PendingWorkspaceUser);

  return (
    <>
      <UserBoxButton
        data-testid="header-userbox-button"
        color="secondary"
        ref={ref}
        onClick={handleOpen}
        aria-label={t("Open user profile menu")}
      >
        <Avatar
          data-testid="header-userbox-avatar"
          variant="rounded"
          alt={getPersonDisplayName(user, router.locale) || user.email}
          src={resolveMediaUrl((user as any)?.profile_pic || (user as any)?.avatar)}
        />
        {/* <Hidden mdDown>
          <UserBoxText>
            <UserBoxLabel variant="body1">{user.name || user.email}</UserBoxLabel>
            <UserBoxDescription variant="body2">{auth.role === 0 ? "ADMIN" : "USER"}</UserBoxDescription>
          </UserBoxText>
        </Hidden> */}
        <Hidden mdDown>
          <ExpandMoreTwoToneIcon sx={{ ml: 1 }} />
        </Hidden>
      </UserBoxButton>
      <Popover
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
      >
        <Card
          sx={{
            background: theme.sidebar.background,
            border: theme.palette.mode === "dark" ? `1px solid ${theme.general.border}` : "none"
          }}
        >
          <MenuUserBox sx={{ minWidth: 210, background: theme.sidebar.background }} display="flex">
            {/* <Avatar variant="rounded" alt={"alt_" + user?.avatar} src={user?.avatar} /> */}
            <UserBoxText>
              {/* email or name */}
              <UserBoxDescription variant="body2">{workspaceDescription}</UserBoxDescription>
              <UserBoxLabel variant="body1">{getPersonDisplayName(user, router.locale) || user.email}</UserBoxLabel>
            </UserBoxText>
          </MenuUserBox>
          {/* <Divider sx={{ mb: 0 }} /> */}
          <List sx={{ p: 1 }} component="nav">
            <ListItem component={"button"} onClick={handleProfileClick}>
              <PersonOutlineOutlinedIcon sx={{ color: theme.sidebar.menuIconColor }} fontSize="small" />
              <ListItemText
                sx={{
                  color:
                    theme.palette.mode === "dark" ? theme.colors.alpha.trueWhite[100] : theme.colors.alpha.black[100]
                }}
                primary={t("My Profile")}
              />
            </ListItem>
            {/* <NextLink href="#" passHref>
            <ListItem button>
              <InboxTwoToneIcon fontSize="small" />
              <ListItemText primary="Messenger" />
            </ListItem>
          </NextLink>
            <NextLink href={PATH.SETUP_WIZARD} passHref>
              <ListItem button>
                <AccountTreeTwoToneIcon sx={{ color: theme.sidebar.menuIconColor }} fontSize="small" />
                <ListItemText sx={{ color: theme.colors.alpha.trueWhite[100] }} primary={t("Setup wizard")} />
              </ListItem>
            </NextLink>*/}
          </List>
          <Divider />
          <Box sx={{ p: 2 }}>
            <Typography
              variant="caption"
              sx={{ fontWeight: "bold", color: theme.palette.secondary.main, mb: 1, display: "block" }}
            >
              {t("Language")}
            </Typography>
            <LanguageSwitcher />

            <Typography
              variant="caption"
              sx={{ fontWeight: "bold", color: theme.palette.secondary.main, mt: 2, mb: 1, display: "block" }}
            >
              {t("Appearance")}
            </Typography>
            <ThemeSwitcher />
          </Box>
          <Divider />
          <Box sx={{ m: 1 }}>
            <CButton
              sx={{
                color:
                  theme.palette.mode === "dark" ? theme.colors.alpha.trueWhite[100] : theme.colors.alpha.black[100],
                justifyContent: "start"
              }}
              fullWidth
              onClick={handleLogout}
            >
              <LoginIcon sx={{ mr: 1, color: theme.sidebar.menuIconColor }} />
              {t("Log out")}
            </CButton>
          </Box>
        </Card>
      </Popover>
    </>
  );
}

export default HeaderUserbox;
